import { FC, useContext, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { Button } from 'antd';

import { createApplicationPayment } from '@/api/create-application-payment';
import { InvoiceModal } from '@/components/TariffsTable/InvoiceModal/InvoiceModal';
import { InvoiceFieldType } from '@/components/TariffsTable/InvoiceModalContent/InvoiceModalContent';
import { ApplicationPaymentType } from '@/constants/application-payments';
import { REDIRECT_TO_QUERY } from '@/constants/authorization';
import { FeedbackContext } from '@/context/feeadback.context';
import { useTranslate } from '@/hooks/use-translate';

import { en } from './en';

import styles from './SearchPageBanner.module.scss';

type Props = {
	title: string;
	descriptions: string[];
	buttonText?: string;
	isRenderLoginBlock?: boolean;
	hideButton?: boolean;
	onButtonClick?: () => void;
};

export const SearchPageBanner: FC<Props> = ({
	title,
	descriptions,
	isRenderLoginBlock,
	buttonText,
	hideButton,
	onButtonClick,
}) => {
	const router = useRouter();

	const [isLoading, setIsLoading] = useState(false);
	const [isOpenInvoiceModal, setIsOpenInvoiceModal] = useState(false);
	const { notify } = useContext(FeedbackContext);

	const t = useTranslate(en);

	const showInvoiceModal = () => {
		setIsOpenInvoiceModal(true);
	};

	const hideInvoiceModal = () => {
		setIsOpenInvoiceModal(false);
	};

	const handleSendInvoice = async (formData: InvoiceFieldType) => {
		const { name, phone, email, comment } = formData;

		try {
			setIsLoading(true);

			await createApplicationPayment({
				type: ApplicationPaymentType.Demo,
				infoForRequest: {
					name,
					phone,
					email,
					comment,
				},
			});

			notify.success({
				message: t('Ваша заявка успешно отправлена'),
			});
			hideInvoiceModal();
		} catch (error) {
			notify.error({
				message: error?.message ?? t('Произошла ошибка при отправке заявки'),
				description: t('Пожалуйста, повторите запрос'),
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className={styles.container} id="banner">
			<h1 className={styles.title}>{t(title)}</h1>
			<div className={styles.descriptionBlock}>
				{descriptions.map((description) => (
					<div key={description} className={styles.description}>
						{t(description)}
					</div>
				))}
			</div>
			{!hideButton && (
				<Button
					data-testid="search-banner-primary-btn"
					className={styles.button}
					type="primary"
					size="large"
					onClick={onButtonClick}
				>
					{t(buttonText)}
				</Button>
			)}
			<div>
				<Link
					className={styles.link}
					href={`/login?${REDIRECT_TO_QUERY}=${router.asPath}`}
					data-testid="search-banner-login-link"
				>
					Войти
				</Link>
			</div>
			<div className={styles.notesBlock}>
				<div className={styles.notes}>
					<div>{t('Хотите протестировать платформу самостоятельно?')}&nbsp;</div>
					<a
						className={styles.link}
						href="https://demo.fishplace.su/login"
						target="_blank"
						rel="noreferrer"
					>
						{t('Демоверсия платформы')}
					</a>
				</div>
				{isRenderLoginBlock && (
					<div className={styles.notes}>
						<div>{t('Нужна помощь и сопровождение?')}&nbsp;</div>
						<div className={styles.link} onClick={() => showInvoiceModal()}>
							{t('Закажите экскурсию')}
						</div>
					</div>
				)}
			</div>
			{isOpenInvoiceModal && (
				<InvoiceModal
					isLoading={isLoading}
					onSendInvoice={handleSendInvoice}
					isOpenModal={isOpenInvoiceModal}
					title={t('Экскурсия по платформе')}
					onHideModal={hideInvoiceModal}
					description="Менеджер Fishplace свяжется с вами — расскажет о преимуществах, покажет возможности, расскажет о том, как выгодно использовать платформу."
				/>
			)}
		</div>
	);
};
